import { useMemo, useState } from 'react';
import {
  DataGridLayout,
  DateRangeFilter,
  Filters,
  FiltersLayout,
  List,
  ListHeader,
  ListPageLayout,
  QuickFilter,
  SearchInput,
} from 'framework/list';
import {
  COMPUTE_PERFORMANCE_BASED_BONUS,
  CREATE_EXPENSE,
  EXPENSE_STATUSES,
  EXPENSE_TYPES,
  READ_EXPENSE,
  useExpenseService,
  useExpenseUrl,
} from 'domain/shared/expense';
import CreateExpenseButton from 'domain/finance/expenses/shared/button/CreateExpenseButton';
import ExpenseLink from 'domain/finance/expenses/shared/field/ExpenseLink';
import ViewExpenseButton from 'domain/finance/expenses/shared/button/ViewExpenseButton';
import { SearchResultField } from 'framework/ui/field';
import ExpenseStatusField from 'domain/finance/expenses/shared/field/ExpenseStatusField';
import ExpenseStatusFilter from 'domain/finance/expenses/shared/filter/ExpenseStatusFilter';
import { EmployeeLinkFieldWithNameLoading, OfficeCodeFilter } from 'domain/shared/employee';
import ExpenseTypeField from 'domain/finance/expenses/shared/field/ExpenseTypeField';
import ExpenseTypeFilter from 'domain/finance/expenses/shared/filter/ExpenseTypeFilter';
import ExpensesSubTypeDropdownFilter from 'domain/finance/expenses/shared/filter/ExpenseSubTypeDropdownFilter';
import { FormFieldsData, ListRowSelectCheckbox } from 'framework/form';
import { useAuthorization } from 'framework/authorization';
import UploadEmployeeExpensesButton from '../shared/button/UploadEmployeeExpensesButton';
import ListPageHeaderWrapper from '../../shared/ListPageHeaderWrapper';
import MarkPayrollExpensesPaidButton from '../shared/button/MarkPayrollExpensesPaidButton';
import RejectListExpenseButton from '../shared/button/RejectListExpenseButton';
import CreateBatchExpenseAccrualCyclesButton from '../shared/button/CreateBatchExpenseAccrualCyclesButton';
import MarkAsPaidExpenseListButton from 'domain/finance/expenses/shared/button/MarkAsPaidExpenseListButton';
import ComputeBonusesButton from 'domain/finance/expenses/shared/button/ComputeBonusesButton';
import DownloadBatchExpenseAccrualVerificationCsvButton from '../shared/button/DownloadBatchExpenseAccrualVerificationCsvButton';
import { EmployeeFilter } from 'domain/shared/employee';

const ExpenseListPage = () => {
  const [checkedRow, setCheckedRow] = useState<FormFieldsData[]>([]);
  const { search } = useExpenseService();
  const { isGranted } = useAuthorization();
  const { getUploadExpensesUrl } = useExpenseUrl();

  const checkedRowIds = useMemo(() => {
    return checkedRow.map(item => item.id);
  }, [checkedRow]);

  const isShowMarkAsPaid = useMemo(() => {
    return !checkedRow.find(item => item.status === EXPENSE_STATUSES.REGISTERED);
  }, [checkedRow]);

  const filters = (
    <Filters>
      <FiltersLayout
        quickFilters={[
          <QuickFilter label="Not Paid Expenses" state={{ status: { in: [EXPENSE_STATUSES.PENDING] } }} />,
        ]}
      >
        <OfficeCodeFilter label="Office" source="office" />
        <EmployeeFilter label="Worker" source="employeeAlias" />
        <ExpenseStatusFilter label="Status" source="status" />
        <ExpenseTypeFilter label="Type" source="type" />
        <ExpensesSubTypeDropdownFilter label="Subtypes" source="subtype" />
        <DateRangeFilter label="Registration date" source="registrationDate" />
      </FiltersLayout>
    </Filters>
  );

  const handleRowSelection = ({ rowData, checked }: FormFieldsData) => {
    if (checked) {
      setCheckedRow(prev => [...prev, rowData]);
    } else {
      setCheckedRow(prev => prev.filter(item => item.id !== rowData.id));
    }
  };

  return (
    <List getDataMethod={search}>
      <ListPageLayout
        header={
          <ListPageHeaderWrapper
            header={<ListHeader label="Worker Expenses" />}
            actions={[
              <UploadEmployeeExpensesButton url={getUploadExpensesUrl()} permissionToCheck={CREATE_EXPENSE} />,
              <MarkPayrollExpensesPaidButton />,
              <MarkAsPaidExpenseListButton
                ids={checkedRowIds}
                isVisible={!!checkedRowIds.length && isShowMarkAsPaid}
              />,
              <CreateBatchExpenseAccrualCyclesButton permissionToCheck={COMPUTE_PERFORMANCE_BASED_BONUS} />,
              <DownloadBatchExpenseAccrualVerificationCsvButton permissionToCheck={COMPUTE_PERFORMANCE_BASED_BONUS} />,
              <ComputeBonusesButton />,
              <RejectListExpenseButton ids={checkedRowIds} isVisible={!!checkedRowIds.length} />,
            ]}
          />
        }
        actions={[<CreateExpenseButton />]}
        filter={filters}
        search={<SearchInput label="title" />}
      >
        <DataGridLayout buttons={[<ViewExpenseButton source="id" isVisible={isGranted(READ_EXPENSE)} />]}>
          <ListRowSelectCheckbox
            source="id"
            label=""
            onChangeCallback={handleRowSelection}
            isVisible={(data: any) =>
              (data.getValueBySource('status') === EXPENSE_STATUSES.PENDING ||
                data.getValueBySource('status') === EXPENSE_STATUSES.REGISTERED) &&
              data.getValueBySource('type') !== EXPENSE_TYPES.PERFORMANCE_BASED_BONUS
            }
            width="62px"
          />
          <ExpenseLink source="id" nameSource="id" label="ID" width="150px" isClickable={isGranted(READ_EXPENSE)} />
          <ExpenseTypeField source="type" label="Type" />
          <SearchResultField source="registrationInfo.title" label="Title" />
          <EmployeeLinkFieldWithNameLoading source="employee.alias" label="Worker" />
          <ExpenseStatusField source="status" label="Status" />
        </DataGridLayout>
      </ListPageLayout>
    </List>
  );
};

export default ExpenseListPage;
